import {useIsFeatureStateEnabled} from '../../hooks/use-feature-state'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {InfoIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {useRef} from 'react'
import {clsx} from 'clsx'
import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'
import {DockedPanel, useDockedPanelActions, useIsDockedPanelOpen} from '../../contexts/DockedPanelContext'

/**
 * Renders a button that opens the overview panel.
 * Panel content is rendered by DockedSidePanelContent.
 */
export function OpenOverviewPanelButton({className, size = 'small'}: {className?: string; size?: 'small' | 'medium'}) {
  const isDockedPanelOpen = useIsDockedPanelOpen(DockedPanel.Overview)
  const actions = useDockedPanelActions()

  const updatedHeader = useIsFeatureStateEnabled('overview')
  const panelLabel = updatedHeader ? 'description' : 'overview'
  const togglePanelRef = useRef<HTMLButtonElement>(null)
  const inactiveAriaLabel = `The ${panelLabel} panel cannot currently be opened.`
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  const overviewButtonAriaLabel = isDockedPanelOpen ? `Close ${panelLabel} panel` : `Open ${panelLabel} panel`

  return (
    <div className={clsx('d-flex flex-items-center', className)}>
      <ErrorBoundary
        boundaryName="OpenOverviewPanelButton"
        fallback={
          <IconButton inactive aria-label={inactiveAriaLabel} icon={InfoIcon} size={size} variant="invisible" />
        }
      >
        <IconButton
          ref={togglePanelRef}
          aria-label={overviewButtonAriaLabel}
          aria-expanded={isDockedPanelOpen}
          icon={InfoIcon}
          size={size}
          variant="invisible"
          onClick={() => {
            sendPullRequestAnalyticsEvent('overview_side_panel.open', 'OVERVIEW_SIDE_PANEL_BUTTON')
            actions.togglePanel('overview', {returnFocusTo: togglePanelRef.current})
          }}
        />
      </ErrorBoundary>
    </div>
  )
}
